"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { ProductFilters } from "./product-filters";
import { useFocusTrap } from "@/hooks/use-focus-trap";
import { useIsClient } from "@/hooks/use-is-client";
import type { Category } from "@/types";

interface MobileFilterDrawerProps {
  categories: Category[];
  categorySlug?: string;
}

export function MobileFilterDrawer({ categories, categorySlug }: MobileFilterDrawerProps) {
  const [open, setOpen] = useState(false);
  const mounted = useIsClient();
  const searchParams = useSearchParams();
  const drawerRef = useFocusTrap(open);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  // Aktif filtre sayısı
  const activeCount = [
    searchParams.get("category") ?? categorySlug,
    searchParams.get("minPrice"),
    searchParams.get("maxPrice"),
    searchParams.get("onSale"),
    searchParams.get("inStock") === "true" ? "inStock" : undefined,
  ].filter(Boolean).length;

  const drawerLayer = (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/45 lg:hidden"
            onClick={() => setOpen(false)}
          />

          {/* Drawer paneli */}
          <motion.div
            key="drawer"
            ref={(el) => { drawerRef.current = el as HTMLElement | null; }}
            role="dialog"
            aria-modal="true"
            aria-label="Filtreler"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
            className="fixed right-0 top-0 flex h-dvh w-[min(88vw,390px)] flex-col border-l border-gray-100 bg-white shadow-2xl dark:border-white/10 dark:bg-[#101010] z-[60] lg:hidden"
          >
            <div className="shrink-0 border-b border-gray-100 px-4 py-4 dark:border-white/10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="font-semibold tracking-tight text-gray-950 dark:text-gray-100">Filtreler</h2>
                  <p className="text-xs text-gray-500">{activeCount ? `${activeCount} aktif filtre` : "Ürünleri daralt"}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 text-gray-400 transition-all hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 dark:border-white/10 dark:hover:bg-white/10 dark:hover:text-white"
                  aria-label="Kapat"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <ProductFilters categories={categories} categorySlug={categorySlug} inDrawer onFilterChange={() => setOpen(false)} />
            </div>

            {/* Uygula butonu */}
            <div className="shrink-0 border-t border-gray-100 bg-white/90 p-4 backdrop-blur-sm dark:border-white/10 dark:bg-[#101010]/90">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex h-12 w-full items-center justify-center rounded-[var(--sf-radius)] bg-[var(--sf-primary)] text-sm font-semibold uppercase tracking-widest text-white transition-all hover:opacity-95 dark:text-black"
              >
                Filtreleri Uygula
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );

  return (
    <>
      {/* Tetikleyici buton */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-11 shrink-0 items-center gap-2 rounded-full border border-gray-200 bg-white/80 px-4 text-sm font-semibold text-gray-700 backdrop-blur-sm transition-all hover:border-gray-400 hover:text-gray-950 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 dark:hover:bg-white/10 lg:hidden"
      >
        <SlidersHorizontal className="h-4 w-4" />
        <span>Filtrele</span>
        {activeCount > 0 && (
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--sf-accent)] px-1 text-[10px] font-bold leading-none text-white">
            {activeCount}
          </span>
        )}
      </button>
      {mounted ? createPortal(drawerLayer, document.body) : drawerLayer}
    </>
  );
}
